import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { FormFieldWrapper } from './FormFieldWrapper';
import { FieldInfoMessage } from './FieldInfoMessage';
import { getAppUrlInfo } from '../../../utils/urls';
import FilePreviewStrip, { FilePreviewItem } from '../../common/FilePreviewStrip';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

export const FileUploadField = (props: any) => {
  const {
    name,
    label,
    disabled,
    required,
    infoMessage,
    multiple = true,
    accept = '',
    allowedExtensions = [],
    maxFileSizeMB = 10,
    maxFiles = 10,
    existingFiles: existingFilesProp = [],
    mode,
  } = props;
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const [localError, setLocalError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { apiUrl } = getAppUrlInfo();

  const selectedFiles: File[] = useMemo(() => {
    const current = values?.[name];
    if (!current) return [];
    if (Array.isArray(current)) return current;
    return current instanceof File ? [current] : [];
  }, [values, name]);

  const existingFiles: string[] = useMemo(() => {
    const initialExistingFiles = Array.isArray(existingFilesProp)
      ? existingFilesProp.map((v: any) => String(v)).filter(Boolean)
      : [];
    const current = values?.existingFiles;
    if (Array.isArray(current)) return current.map((v) => String(v)).filter(Boolean);
    return initialExistingFiles;
  }, [values, existingFilesProp]);

  const initialExistingFiles: string[] = useMemo(
    () => (Array.isArray(existingFilesProp) ? existingFilesProp.map((v: any) => String(v)).filter(Boolean) : []),
    [existingFilesProp]
  );

  const removedExistingFiles: string[] = useMemo(() => {
    const current = values?.removedExistingFiles;
    if (!Array.isArray(current)) return [];
    return current.map((v) => String(v)).filter(Boolean);
  }, [values]);

  const availableSlots = Math.max(0, maxFiles - existingFiles.length - selectedFiles.length);
  const hasFileChanges = selectedFiles.length > 0 || removedExistingFiles.length > 0;

  const isImageFileName = (fileName: string): boolean => {
    const ext = String(fileName).split('.').pop()?.toLowerCase() || '';
    return IMAGE_EXTENSIONS.includes(ext);
  };

  const getFileUrl = (fileName: string) => `${apiUrl}/files/documents/${encodeURIComponent(fileName)}`;
  const getExtLabel = (fileName: string) => fileName.split('.').pop()?.toUpperCase() || 'FILE';

  const validateFile = (file: File): string => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (allowedExtensions.length > 0 && !allowedExtensions.includes(ext)) {
      return `Invalid file type for "${file.name}". Allowed: ${allowedExtensions.join(', ')}`;
    }
    const maxBytes = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return `File "${file.name}" exceeds ${maxFileSizeMB}MB limit`;
    }
    return '';
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      setFieldValue(name, multiple ? [] : null);
      setFieldTouched(name, true, false);
      setLocalError('');
      event.target.value = '';
      return;
    }

    const normalizedFiles = multiple ? files : [files[0]];
    if (normalizedFiles.length + existingFiles.length > maxFiles) {
      setFieldValue(name, multiple ? [] : null);
      setFieldTouched(name, true, false);
      setLocalError(`You can upload up to ${maxFiles} files in total.`);
      event.target.value = '';
      return;
    }

    for (const file of normalizedFiles) {
      const error = validateFile(file);
      if (error) {
        setFieldValue(name, multiple ? [] : null);
        setFieldTouched(name, true, false);
        setLocalError(error);
        event.target.value = '';
        return;
      }
    }

    setLocalError('');
    setFieldValue(name, multiple ? normalizedFiles : normalizedFiles[0]);
    setFieldTouched(name, true, false);
    event.target.value = '';
  };

  const handleRemoveExistingFile = (fileName: string, index: number) => {
    const nextExistingFiles = existingFiles.filter((_, i) => i !== index);
    const removedSet = new Set<string>([...removedExistingFiles, fileName]);
    setFieldValue('existingFiles', nextExistingFiles);
    setFieldValue('removedExistingFiles', Array.from(removedSet));
    setFieldTouched(name, true, false);
    setLocalError('');
  };

  const handleResetFileChanges = () => {
    setFieldValue(name, multiple ? [] : null);
    setFieldValue('existingFiles', initialExistingFiles);
    setFieldValue('removedExistingFiles', []);
    setFieldTouched(name, true, false);
    setLocalError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemoveSelectedFile = (index: number) => {
    const nextFiles = selectedFiles.filter((_, i) => i !== index);
    setFieldValue(name, multiple ? nextFiles : nextFiles[0] || null);
    setFieldTouched(name, true, false);
    setLocalError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const existingPreviewItems: FilePreviewItem[] = useMemo(
    () =>
      existingFiles.map((fileName, index) => ({
        key: `${fileName}-${index}`,
        name: fileName,
        url: getFileUrl(fileName),
        isImage: isImageFileName(fileName),
        extLabel: getExtLabel(fileName),
        onRemove: disabled ? undefined : () => handleRemoveExistingFile(fileName, index),
      })),
    [existingFiles, disabled, removedExistingFiles]
  );

  const selectedPreviewItems: FilePreviewItem[] = useMemo(
    () =>
      selectedFiles.map((file, index) => ({
        key: `${file.name}-${index}-${file.lastModified}`,
        name: file.name,
        url: URL.createObjectURL(file),
        isImage: isImageFileName(file.name),
        extLabel: getExtLabel(file.name),
        badgeText: 'New',
        onRemove: disabled ? undefined : () => handleRemoveSelectedFile(index),
      })),
    [selectedFiles, disabled]
  );

  const previewItems: FilePreviewItem[] = useMemo(
    () => [...existingPreviewItems, ...selectedPreviewItems],
    [existingPreviewItems, selectedPreviewItems]
  );
  const isViewMode = mode === 'view';
  const viewModeItems: FilePreviewItem[] = useMemo(
    () => existingPreviewItems,
    [existingPreviewItems]
  );

  useEffect(() => {
    return () => {
      selectedPreviewItems.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [selectedPreviewItems]);

  return (
    <FormFieldWrapper name={name} label={label} required={required}>
      {isViewMode ? (
        <FilePreviewStrip items={viewModeItems} disabled showNames={false} />
      ) : (
        <>
      <div className="space-y-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
          <div className="min-w-[260px]">
            <input
              ref={inputRef}
              id={name}
              name={name}
              type="file"
              multiple={multiple}
              accept={accept}
              disabled={disabled}
              onChange={handleChange}
              className="block w-full cursor-pointer text-sm text-gray-700 file:cursor-pointer file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium hover:file:bg-gray-200"
            />
            <p className="text-xs text-gray-500 mt-1">
              Limit: {maxFiles} total ({existingFiles.length} existing, {selectedFiles.length} selected, {availableSlots} available)
            </p>
            {hasFileChanges && !disabled && (
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  handleResetFileChanges();
                }}
                className="inline-block mt-1 text-xs text-blue-600 hover:text-blue-800"
              >
                Reset
              </a>
            )}
          </div>

          <FilePreviewStrip label="Files" items={previewItems} disabled={disabled} />
        </div>
      </div>
      {localError && <p className="text-xs text-red-600 mt-1">{localError}</p>}
      {selectedFiles.length > 0 && !localError && (
        <p className="text-xs text-green-700 mt-1">
          Selected ({selectedFiles.length}): {selectedFiles.map((f) => f.name).join(', ')}
        </p>
      )}
      {infoMessage && <FieldInfoMessage message={infoMessage} />}
        </>
      )}
    </FormFieldWrapper>
  );
};
